import React, { FormEvent, useState } from 'react';
import styled, { keyframes } from 'styled-components';

interface ModalRegistrationProps {
  show: boolean;
  handleClose: () => void;
  handleSubmit: (e: FormEvent) => Promise<void>;
}

const ModalRegistration: React.FC<ModalRegistrationProps> = ({ show, handleClose, handleSubmit }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const nextStep = () => setStep(prevStep => (prevStep < 4 ? prevStep + 1 : prevStep));
  const prevStep = () => setStep(prevStep => (prevStep > 1 ? prevStep - 1 : prevStep));
  const goToStep = (stepNumber: number) => setStep(stepNumber);

  const handleFinalSubmit = async (e: FormEvent) => {
    setIsLoading(true);
    await handleSubmit(e);
    setIsLoading(false);
  };

  if (!show) {
    return null;
  }

  return (
    <>
      <ModalOverlay>
        <Container>
          <Header>
            <span>
              <Title>Criação de Conta</Title>
            </span>
          </Header>
          <StepIndicator>
            {[1, 2, 3, 4].map((stepNumber) => (
              <StepItem
                key={stepNumber}
                active={stepNumber <= step}
                onClick={() => goToStep(stepNumber)}
              >
                {stepNumber}
              </StepItem>
            ))}
            <ProgressBar>
              <ProgressLine step={step} />
            </ProgressBar>
          </StepIndicator>
          <Content>
            {step === 1 && (
              <Step>
                <p>
                  Conforme Termos de Uso, declaro possuir uma conta corrente ou
                  conta poupança, com TITULAR DE MESMO CPF cadastrado no site Meu
                  Nascimento, em um dos BANCOS ACEITOS para realização de
                  transferência.
                </p>
                <ButtonContainer>
                  <PrevButton onClick={handleClose}>Cancelar</PrevButton>
                  <NextButton onClick={nextStep}>Próximo</NextButton>
                </ButtonContainer>
              </Step>
            )}
            {step === 2 && (
              <Step>
                <p>A lista de bancos aceitos são:</p>
                <ul>
                  <li>Banco do Brasil</li>
                  <li>Santander</li>
                  <li>Inter</li>
                  <li>Caixa Econômica Federal</li>
                  <li>Stone</li>
                  <li>Banco BTG</li>
                  <li>Bradesco</li>
                  <li>Nubank</li>
                  <li>C6</li>
                  <li>Itaú</li>
                  <li>Safra</li>
                </ul>
                <ButtonContainer>
                  <PrevButton onClick={prevStep}>Voltar</PrevButton>
                  <NextButton onClick={nextStep}>Próximo</NextButton>
                </ButtonContainer>
              </Step>
            )}
            {step === 3 && (
              <Step>
                <p>
                  Em virtude da segurança exigida pela operadora Pagar.me (Banco
                  Stone), NÃO É POSSÍVEL ALTERAR o CPF após confirmação do
                  cadastro.
                </p>
                <ButtonContainer>
                  <PrevButton onClick={prevStep}>Voltar</PrevButton>
                  <NextButton onClick={nextStep}>Próximo</NextButton>
                </ButtonContainer>
              </Step>
            )}
            {step === 4 && (
              <Step>
                <p>
                  Não serão solicitadas pelo Nossa Vaquinha, ou pela Pagar.me, em
                  hipótese alguma, quaisquer tipos de senhas pessoais ao usuário
                  do Nossa Vaquinha.
                </p>
                <p>
                  O único canal oficial para comunicação com o cliente é através
                  do e-mail: contato@nossavaquinha.com.br
                </p>
                <ButtonContainer>
                  <PrevButton onClick={prevStep}>Voltar</PrevButton>
                  <ConfirmButton onClick={handleFinalSubmit}>
                    {isLoading ? <LoadingIcon /> : 'De Acordo e Finalizar Cadastro'}
                  </ConfirmButton>
                </ButtonContainer>
              </Step>
            )}
          </Content>
        </Container>
      </ModalOverlay>
    </>
  );
};

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const LoadingIcon = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #5cc9aa;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: ${rotate} 1s linear infinite;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999999;
`;

const Container = styled.div`
  background-color: #ffffff;
  padding: 2rem;
  border-radius: 13px;
  display: flex;
  flex-direction: column;
  width: 90%;
  max-width: 600px;
  max-height: 90%;
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  border-bottom: solid 1px #ccc;
  padding-bottom: 1rem;

  & > span:first-child {
    margin-right: 1rem;
  }
`;

const Title = styled.h1`
  white-space: nowrap;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0;
  position: relative;
`;

const StepItem = styled.div<{ active: boolean }>`
  width: 30px;
  height: 30px;
  background-color: ${({ active }) => (active ? '#5cc9aa' : '#ccc')};
  color: #fff;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  cursor: pointer;
  z-index: 1;
`;

const ProgressBar = styled.div`
  position: absolute;
  width: 100%;
  height: 4px;
  background-color: #ccc;
  top: 50%;
  transform: translateY(-50%);
  z-index: 0;
`;

const ProgressLine = styled.div<{ step: number }>`
  width: ${({ step }) => (step / 4) * 100}%;
  height: 100%;
  background-color: #5cc9aa;
`;

const Content = styled.div`
  font-size: 11pt;
  padding: 1rem 0;
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`;

const NextButton = styled.button`
  background-color: #5cc9aa;
  color: #ffffff;
  padding: 12px 25px;
  font-family: 'Nunito';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  outline: none;
  border: none;
  border-radius: 8px;
  cursor: pointer;
`;

const PrevButton = styled(NextButton)`
  background-color: gray;

  &:hover {
    background: darkgray;
  }
`;

const ConfirmButton = styled(NextButton)`
  background-color: #5cc9aa;

  &:hover {
    background: #4da88f;
  }
`;

export default ModalRegistration;
